import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Upload,
  message,
  Space,
  Card,
  Image,
  Switch,
  Row,
  Col,
  Select
} from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import companySettingsApi from "../api/companySettingsApi";
import "./SettingsPage.scss";

const CompanySettingsPage = () => {
  const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(true);

  // White Label Options state
  const [whiteLabelEntitlement, setWhiteLabelEntitlement] = useState(null);
  const [whiteLabelSettings, setWhiteLabelSettings] = useState({
    showLogo: true
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Route Settings state
  const [defaultRouteVisibility, setDefaultRouteVisibility] = useState("all");
  const [savingRouteSettings, setSavingRouteSettings] = useState(false);

  const WHITE_LABEL_ENTITLEMENT_ID = "a030b9c2-daf1-40b0-81a6-d943cd60c49c";

  const validateLogoFileType = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic"
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  };

  const validateLogoFileSize = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  const beforeLogoUpload = (file) => {
    if (!validateLogoFileType(file)) {
      message.error("Only JPG, JPEG, PNG, WebP, and HEIC image files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (!validateLogoFileSize(file)) {
      message.error("File size must be less than 5MB");
      return Upload.LIST_IGNORE;
    }

    setSelectedLogoFile(file);
    setShowLogoUploadModal(true);
    return false; // Prevent automatic upload
  };

  // Load company data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load logo
        const logoData = await companySettingsApi.getLogo();
        if (logoData && logoData.url) {
          setCompanyLogo(logoData.url);
        }
      } catch (error) {
        console.log("No logo found or error loading logo:", error);
        // This is expected when no logo is uploaded yet
      }

      try {
        // Load white label entitlement
        const entitlementData = await companySettingsApi.getEntitlement(
          WHITE_LABEL_ENTITLEMENT_ID
        );
        setWhiteLabelEntitlement(entitlementData);

        if (entitlementData.hasEntitlement && entitlementData.settings) {
          setWhiteLabelSettings({
            showLogo:
              entitlementData.settings.showLogo !== undefined
                ? entitlementData.settings.showLogo === "1"
                : true
          });
        }
      } catch (error) {
        console.log("Error loading entitlement:", error);
        setWhiteLabelEntitlement({ hasEntitlement: false, isActive: false });
      }

      try {
        // Load client settings (including defaultRouteVisibility)
        const clientSettings = await companySettingsApi.getClientSettings();
        if (clientSettings && clientSettings.defaultRouteVisibility) {
          setDefaultRouteVisibility(clientSettings.defaultRouteVisibility);
        }
      } catch (error) {
        console.log("Error loading client settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) {
      message.error("Please select a logo file");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", selectedLogoFile);

      const response = await companySettingsApi.uploadLogo(formData);
      message.success("Company logo uploaded successfully!");

      // Update the logo display with the new logo URL
      setCompanyLogo(response.url);

      // Reset modal state
      setShowLogoUploadModal(false);
      setSelectedLogoFile(null);

      // Dispatch custom event to update sidebar logo
      window.dispatchEvent(new CustomEvent("companyLogoUpdated"));
    } catch (error) {
      console.error("Error uploading logo:", error);
      message.error("Failed to upload logo. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveWhiteLabelSettings = async () => {
    setSavingSettings(true);
    try {
      const settings = {
        showLogo: whiteLabelSettings.showLogo ? "1" : "0"
      };

      await companySettingsApi.updateEntitlementSettings(
        WHITE_LABEL_ENTITLEMENT_ID,
        settings
      );
      message.success("White label settings saved successfully!");

      // Dispatch custom event to update sidebar logo
      window.dispatchEvent(new CustomEvent("whiteLabelSettingsUpdated"));
    } catch (error) {
      console.error("Error saving settings:", error);
      message.error("Failed to save settings. Please try again.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveRouteSettings = async () => {
    setSavingRouteSettings(true);
    try {
      await companySettingsApi.updateClientSettings({
        defaultRouteVisibility
      });
      message.success("Route settings saved successfully!");
    } catch (error) {
      console.error("Error saving route settings:", error);
      message.error("Failed to save route settings. Please try again.");
    } finally {
      setSavingRouteSettings(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="setting-content">
        <div className="common-form">
          <h3>Company Settings</h3>

          {/* Company Logo Section */}
          <Card title="Company Logo" style={{ marginBottom: 24 }} loading={loading}>
            <Row gutter={24}>
              <Col span={12}>
                <div
                  style={{
                    width: 280,
                    height: 280,
                    border: "2px dashed #d9d9d9",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fafafa",
                    overflow: "hidden"
                  }}
                >
                  {companyLogo ? (
                    <Image
                      src={companyLogo}
                      alt="Company Logo"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block"
                      }}
                      preview={true}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#8c8c8c" }}>
                      <PlusOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <div>No logo uploaded</div>
                      <div style={{ fontSize: "12px" }}>Upload your company logo</div>
                    </div>
                  )}
                </div>

                <div>
                  <Upload
                    beforeUpload={beforeLogoUpload}
                    showUploadList={false}
                    accept=".jpg,.jpeg,.png,.webp,.heic"
                  >
                    <Button icon={<UploadOutlined />} type="primary">
                      {companyLogo ? "Change Logo" : "Upload Logo"}
                    </Button>
                  </Upload>
                  <div style={{ marginTop: 8, fontSize: "12px", color: "#8c8c8c" }}>
                    Supported formats: JPG, JPEG, PNG, WebP, HEIC
                    <br />
                    Maximum size: 5MB
                  </div>
                </div>
              </Col>
              <Col span={12}>
                {/* White Label Options */}
                <Card title="White Label Options" size="small" style={{ height: "100%" }}>
                  {whiteLabelEntitlement &&
                  whiteLabelEntitlement.hasEntitlement &&
                  whiteLabelEntitlement.isActive ? (
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8
                          }}
                        >
                          <span>Show logo in place of Flexio?</span>
                          <Switch
                            checked={whiteLabelSettings.showLogo}
                            onChange={(checked) =>
                              setWhiteLabelSettings({
                                ...whiteLabelSettings,
                                showLogo: checked
                              })
                            }
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8
                          }}
                        >
                          <span style={{ color: "#8c8c8c" }}>
                            Custom Domain: coming soon
                          </span>
                          <Switch disabled={true} />
                        </div>
                      </div>

                      <Button
                        type="primary"
                        onClick={handleSaveWhiteLabelSettings}
                        loading={savingSettings}
                        style={{ width: "100%" }}
                      >
                        Save Settings
                      </Button>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <p style={{ marginBottom: 16, color: "#666" }}>
                        Need to whitelabel Flexio?
                      </p>
                      <Link to="/billing/upgrades">
                        <Button type="primary">Enable this option now</Button>
                      </Link>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Route Settings Section */}
          <Card title="Route Settings" style={{ marginBottom: 24 }} loading={loading}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Default Route Visibility
              </div>
              <div style={{ marginBottom: 16, color: "#666", fontSize: 14 }}>
                This setting controls which drivers can see newly created routes by
                default. You can adjust individual routes after creation.
              </div>
              <Select
                value={defaultRouteVisibility}
                onChange={setDefaultRouteVisibility}
                style={{ width: "100%", marginBottom: 16 }}
              >
                <Select.Option value="all">All Drivers</Select.Option>
                <Select.Option value="internal_only">Internal Drivers Only</Select.Option>
                <Select.Option value="manual_only">Manual Assign Only</Select.Option>
              </Select>
              <Button
                type="primary"
                onClick={handleSaveRouteSettings}
                loading={savingRouteSettings}
              >
                Save Route Settings
              </Button>
            </div>
          </Card>

          <p>Other company settings functionality will be implemented here.</p>
        </div>
      </div>

      {/* Logo Upload Modal */}
      <Modal
        title="Upload Company Logo"
        open={showLogoUploadModal}
        onCancel={() => {
          setShowLogoUploadModal(false);
          setSelectedLogoFile(null);
        }}
        width={500}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>File Details:</strong>
          </p>
          {selectedLogoFile && (
            <div
              style={{
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
                marginBottom: 16
              }}
            >
              <div>
                <strong>Name:</strong> {selectedLogoFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedLogoFile.size / 1024).toFixed(1)} KB
              </div>
              <div>
                <strong>Type:</strong> {selectedLogoFile.type}
              </div>
            </div>
          )}

          <div
            style={{
              padding: 12,
              backgroundColor: "#e6f7ff",
              borderRadius: 4,
              marginBottom: 16,
              fontSize: "12px"
            }}
          >
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Allowed file types:</strong>
            </p>
            <p style={{ margin: 0 }}>
              • Images: JPG, JPEG, PNG, WebP, HEIC
              <br />• Max size: 5MB
            </p>
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <Space>
            <Button
              onClick={() => {
                setShowLogoUploadModal(false);
                setSelectedLogoFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={uploadingLogo}
              disabled={!selectedLogoFile}
              onClick={handleLogoUpload}
              icon={<UploadOutlined />}
            >
              {uploadingLogo ? "Uploading..." : "Upload Logo"}
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default CompanySettingsPage;
